import graphql__f7af5194c1536a0bb5b1 from "./__generated__/AppAuthAPIConnectionSectionQuery.graphql.ts";import { Badge, Card, TwoColumn } from '@portal/components'
import graphql from 'babel-plugin-relay/macro'
import ReadTheDocsLink from 'components/docsLink/ReadTheDocsLink'
import React, { Suspense, useEffect } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'

import CopyableEntry from '../../../../components/copyableEntry'
import { AppAuthAPIConnectionSectionQuery } from './__generated__/AppAuthAPIConnectionSectionQuery.graphql'
import ApiKeyManager from './apiKey/ApiKeyManager'

export const query = graphql__f7af5194c1536a0bb5b1

type ContainerProps = {
  appId: string
}

export default function AppAuthAPIConnectionSectionContainer({
  appId,
}: ContainerProps) {
  const [queryRef, loadQuery] =
    useQueryLoader<AppAuthAPIConnectionSectionQuery>(query)

  useEffect(() => {
    if (appId) {
      loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
    }
  }, [appId, loadQuery])

  if (!queryRef || !appId) {
    return null
  }

  return (
    <Suspense>
      <AppAuthAPIConnectionSection appId={appId} queryRef={queryRef} />
    </Suspense>
  )
}

type Props = {
  queryRef: PreloadedQuery<AppAuthAPIConnectionSectionQuery>
} & ContainerProps

function AppAuthAPIConnectionSection({
  appId,
  queryRef,
}: Props): React.ReactElement {
  const { viewer } = usePreloadedQuery(query, queryRef)

  const cloudUrls = viewer.appById?.cloudUrls || []
  const wsEnabled = cloudUrls[0]?.wsEnabled
  const httpEnabled = cloudUrls[0]?.httpEnabled

  const canReadApiKeys = !!viewer.appById?.viewerCanReadApiKey
  const canCreateApiKeys = !!viewer.appById?.viewerCanCreateApiKey
  const canDeleteApiKeys = !!viewer.appById?.viewerCanDeleteApiKey

  const renderColumnContent = () => {
    return (
      <p>
        Everything you need to get connected to Ditto through our HTTP API.{' '}
        <ReadTheDocsLink docsArea="httpApi" />
      </p>
    )
  }

  const renderAvailableAPIKeyActions = () => {
    const canFullyManageApiKeys = canCreateApiKeys && canDeleteApiKeys

    if (canFullyManageApiKeys) {
      return 'Create and manage'
    } else if (canCreateApiKeys) {
      return 'Create and view'
    } else if (canReadApiKeys) {
      return 'View'
    }
  }

  return (
    <TwoColumn
      firstColumnTitle="Connect via HTTP"
      firstColumnContent={renderColumnContent()}
    >
      <Suspense fallback={null}>
        <div className="flex flex-col gap-8">
          <Card isDivided={false}>
            <Card.Header>
              <div className="flex flex-col gap-1 pb-4">
                <h3 className="block font-sans text-lg font-medium text-foreground-normal">
                  Big peer connection information
                </h3>
                <p className="font-sans text-base font-normal text-foreground-subtle">
                  Connect to Ditto with your cloud URL
                </p>
              </div>
            </Card.Header>
            <Card.Body className="flex flex-col gap-3">
              <CopyableEntry
                title="Cloud URL Endpoint"
                testId="cloudUrl"
                value={cloudUrls[0]?.url}
                render={() =>
                  cloudUrls.map((cloudUrl) => (
                    <div key={cloudUrl.url}>{cloudUrl.url}</div>
                  ))
                }
                isCode
              />

              <div className="flex flex-wrap items-center gap-3">
                <Badge colorScheme={httpEnabled ? 'green' : 'red'}>
                  HTTP {httpEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <Badge colorScheme={wsEnabled ? 'green' : 'red'}>
                  Websocket {wsEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </Card.Body>
          </Card>

          {canReadApiKeys && (
            <Card isDivided={false}>
              <Card.Header>
                <div className="flex flex-col gap-1">
                  <h3 className="block font-sans text-lg font-medium text-foreground-normal">
                    API keys
                  </h3>
                  <p className="font-sans text-base font-normal text-foreground-subtle">
                    {renderAvailableAPIKeyActions()} API keys to authenticate
                    via HTTP. <ReadTheDocsLink docsArea="apiKey" />
                  </p>
                </div>
              </Card.Header>
              <Card.Spacer />
              <Card.Body isFlushed>
                <ApiKeyManager appId={appId} />
              </Card.Body>
            </Card>
          )}
        </div>
      </Suspense>
    </TwoColumn>
  )
}
